import { CHIP_TYPE, RotationCarouselDisabledState } from '@sb/cc-design-system';
import dayjs from 'dayjs';
import type { IProducts } from 'gcf-components/*';
import {
  IAssemblyComponent,
  IComponent,
  IComponents,
  IScheduleItemOverrides,
  IScheduleSection,
  RotationEngineStatus,
} from 'gcf-player/shared-utils';
import { escapeRegExp, isEqual, isNil } from 'lodash';
import {
  ComponentInstanceData,
  EComponentInstanceDataTarget,
} from 'src/components/gcfPreview/GcfPreview.interface';
import { US_DATE_SLASH_FORMAT } from 'src/constants';
import { onDateRangeValidate } from 'src/features/locations/scheduleComponentPage/modules/componentAccordion/ComponentAccordion.helper';
import { IGcfComponentConfig } from 'src/interfaces/Component';
import { ComponentTypes } from 'src/interfaces/ComponentTypesList';
import {
  IGcfProductListGroup,
  IGcfProductListProduct,
  IGcfProductListProductImage,
  IProduct,
  IProductVariation,
} from 'src/interfaces/Product';
import {
  ISchedulePreviewContext,
  ISchedulePreviewWithChangeSetContext,
} from 'src/interfaces/SchedulePreview';
import { formatIsoDateOrNull, moveArrayItem } from 'src/utils';
import { FileTypes, getFileExtensionType } from 'src/utils/file-types';
import {
  mapGcfGroupProducts,
  mapGCFProductArrayToGcfGroupProducts,
  mapProductToGCFGroupProduct,
  sortItemsByOrder,
} from 'src/utils/product';
import { isProductImageVisible } from 'src/utils/product-attributes';
import { uid } from 'uid';

import {
  ChangeItem,
  ChangeOperation,
  ChangeSetPayload,
  ChangeTypeEnum,
} from '../scheduleComponentPage/ScheduleComponentPage.interface';
import {
  IComponentWithTheme,
  IOverrideChangeSet,
  IOverrideValidationChips,
  IPreviewChangeSetIds,
  IProductImageStyleAndOutageStamp,
  IProductVariationsAssets,
  ISelectedSectionDetailInfo,
} from './MenuBoardModificationPage.interface';
import { getOverrideDatePickerLimits } from './modules/overrideLeftSideBar/OverrideLeftSideBar.helper';
import { transformProductKeyToOrderKey } from './modules/productOverrideRightSideBar/ProductOverrideRightSideBar.config';
import {
  IAttributeSize,
  mapVariationsToAttributes,
} from './modules/productOverrideRightSideBar/ProductOverrideRightSideBar.helper';

const SEARCH_FILTER_PREFIX = 'name.regexp=';
const SEARCH_TAG_FILTER = 'with_component_tag_name=true';

type ILineupProductVariationInfo = IProducts[string]['variations'][string];
type IOptimisticPreviewComponent = IComponent & {
  component_type?: {
    name?: string;
  };
};

export const getComponentThemeName = (component?: unknown) => {
  const theme = (component as IComponentWithTheme | undefined)?.theme;

  return typeof theme === 'string' ? theme : theme?.name;
};

const getLatestScheduleItemOverrideModifiedAt = (
  scheduleItems: IScheduleItemOverrides,
) =>
  Object.values(scheduleItems).reduce((latestModifiedAt, overrides) => {
    if (!Array.isArray(overrides)) {
      return latestModifiedAt;
    }

    return overrides.reduce((latestOverrideModifiedAt, override) => {
      const modifiedAt = dayjs(override.modifiedAt).valueOf();

      return Number.isFinite(modifiedAt)
        ? Math.max(latestOverrideModifiedAt, modifiedAt)
        : latestOverrideModifiedAt;
    }, latestModifiedAt);
  }, 0);

const getPreviewComponentType = (component?: IOptimisticPreviewComponent) => {
  return component?.type ?? component?.component_type?.name;
};

const getPreviewComponentTheme = (component?: IOptimisticPreviewComponent) => {
  return getComponentThemeName(component);
};

const getOptimisticPreviewComponent = (component?: IComponent) => {
  if (!component) {
    return undefined;
  }

  const previewComponent = component as IOptimisticPreviewComponent;
  const componentType = getPreviewComponentType(previewComponent);

  if (!componentType) {
    return undefined;
  }

  return {
    ...previewComponent,
    type: componentType,
    theme: getPreviewComponentTheme(previewComponent),
    config: previewComponent.config,
    name: previewComponent.name,
    width: previewComponent.width,
    height: previewComponent.height,
  };
};

export const getPreviewProducts = (
  schedulePreviewContext?:
    | ISchedulePreviewContext
    | ISchedulePreviewWithChangeSetContext,
) => {
  if (!schedulePreviewContext) {
    return undefined;
  }

  return 'after' in schedulePreviewContext
    ? schedulePreviewContext.after.products
    : schedulePreviewContext.products;
};

export const getPreviewContext = (
  schedulePreviewContext?:
    | ISchedulePreviewContext
    | ISchedulePreviewWithChangeSetContext,
): ISchedulePreviewContext | undefined => {
  if (!schedulePreviewContext) {
    return undefined;
  }

  return 'after' in schedulePreviewContext
    ? schedulePreviewContext.after
    : schedulePreviewContext;
};

export const applyOptimisticProductOverridePreview = (
  previewContext: ISchedulePreviewContext | undefined,
  overrideChangeSets: IOverrideChangeSet[],
  lineupFallbackProducts: IProducts = {},
  activePreviewOverrideId?: string | number,
): ISchedulePreviewContext | undefined => {
  if (!previewContext) {
    return previewContext;
  }

  const optimisticOverrides = overrideChangeSets.filter(({ changeSetItem }) => {
    const isActivePreviewOverride =
      activePreviewOverrideId !== undefined &&
      String(changeSetItem.id) === String(activePreviewOverrideId);
    const isActivePreviewProductDraft =
      isActivePreviewOverride &&
      Boolean(
        (changeSetItem.component?.config as IGcfComponentConfig)?.productList,
      );
    const isActivePreviewPromoDraft =
      isActivePreviewOverride &&
      getPreviewComponentType(changeSetItem.component) ===
        ComponentTypes.PROMOTIONAL_SPACE;
    const isUnsavedOverride =
      isNil(changeSetItem.scheduleOverrideId) ||
      getScheduleOverrideStatus(changeSetItem.scheduleOverrideId);
    const isMissingEndDate = !changeSetItem.endDate;

    return (
      (changeSetItem.hasProductEdit ||
        isActivePreviewProductDraft ||
        isActivePreviewPromoDraft) &&
      !(isUnsavedOverride && isMissingEndDate) &&
      Boolean(changeSetItem.scheduleItemId) &&
      Boolean(changeSetItem.component?.config)
    );
  });

  if (
    !optimisticOverrides.length &&
    !Object.keys(lineupFallbackProducts).length
  ) {
    return previewContext;
  }

  const currentScheduleItems = previewContext.overrides?.scheduleItems ?? {};
  const nextScheduleItems = { ...currentScheduleItems };
  const optimisticComponents = optimisticOverrides.reduce(
    (components, { changeSetItem }) => {
      const componentId =
        changeSetItem.componentId ?? changeSetItem.component?.id;

      if (
        !isNil(componentId) &&
        !previewContext.components?.[String(componentId)]
      ) {
        const optimisticComponent = getOptimisticPreviewComponent(
          changeSetItem.component,
        );

        if (optimisticComponent) {
          components[String(componentId)] = optimisticComponent;
        }
      }

      return components;
    },
    {} as IComponents,
  );
  const latestPreviewModifiedAt =
    getLatestScheduleItemOverrideModifiedAt(currentScheduleItems);
  const optimisticModifiedAtBase = Math.max(
    Date.now(),
    latestPreviewModifiedAt + optimisticOverrides.length + 1,
  );
  const hasActivePreviewOverride =
    activePreviewOverrideId !== undefined &&
    optimisticOverrides.some(
      ({ changeSetItem }) =>
        String(changeSetItem.id) === String(activePreviewOverrideId),
    );

  optimisticOverrides.forEach(({ changeSetItem }, optimisticIndex) => {
    const scheduleItemId = String(changeSetItem.scheduleItemId);
    const currentOverrides = nextScheduleItems[scheduleItemId] ?? [];
    const isActivePreviewOverride =
      activePreviewOverrideId !== undefined &&
      String(activePreviewOverrideId) === String(changeSetItem.id);
    const matchingPreviewOverride = currentOverrides.find(
      (override: { scheduleItemOverrideId?: string | number }) =>
        String(override.scheduleItemOverrideId) === String(changeSetItem.id),
    );
    const hasPreviewCaughtUp =
      matchingPreviewOverride &&
      matchingPreviewOverride.startDate ===
        formatIsoDateOrNull(changeSetItem.startDate) &&
      matchingPreviewOverride.endDate ===
        formatIsoDateOrNull(changeSetItem.endDate) &&
      isEqual(matchingPreviewOverride.config, changeSetItem.component?.config);

    if (hasPreviewCaughtUp && !isActivePreviewOverride) {
      return;
    }

    const modifiedAtOffset = hasActivePreviewOverride
      ? isActivePreviewOverride
        ? 0
        : optimisticIndex + 1
      : optimisticIndex;

    const optimisticScheduleItemOverride = {
      scheduleItemOverrideId: changeSetItem.id,
      startDate: formatIsoDateOrNull(changeSetItem.startDate),
      endDate: formatIsoDateOrNull(changeSetItem.endDate),
      componentId: changeSetItem.componentId ?? null,
      // GCF chooses between overlapping active overrides by latest modifiedAt.
      // The active preview draft must win; otherwise overrideChangeSets is newest-first.
      modifiedAt:
        changeSetItem.modifiedAt ??
        new Date(optimisticModifiedAtBase - modifiedAtOffset).toISOString(),
      config: changeSetItem.component?.config ?? null,
    };
    const existingIndex = currentOverrides.findIndex(
      (override: { scheduleItemOverrideId?: string | number }) =>
        String(override.scheduleItemOverrideId) === String(changeSetItem.id),
    );

    nextScheduleItems[scheduleItemId] =
      existingIndex >= 0
        ? currentOverrides.map((override: unknown, index: number) =>
            index === existingIndex ? optimisticScheduleItemOverride : override,
          )
        : [...currentOverrides, optimisticScheduleItemOverride];
  });

  const nextPreviewContext = {
    ...previewContext,
    products: {
      ...(previewContext.products ?? {}),
      ...lineupFallbackProducts,
    },
    overrides: {
      ...(previewContext.overrides ?? {}),
      scheduleItems: nextScheduleItems,
    },
  };

  if (Object.keys(optimisticComponents).length) {
    nextPreviewContext.components = {
      ...(previewContext.components ?? {}),
      ...optimisticComponents,
    };
  }

  return nextPreviewContext;
};

const getLineupFallbackVariation = (
  variation: IProductVariation,
): ILineupProductVariationInfo =>
  ({
    productCode: variation.product_code,
    size: variation.productVariationSize?.name ?? '',
    shortCode: variation.productVariationSize?.short ?? '',
    flavor: variation.productVariationFlavor?.name ?? '',
    name: variation.name,
    active: variation.active ?? true,
    createdAt: variation.created_at,
    assetName:
      variation.assets?.[0]?.name ||
      variation.variationAssets?.[0]?.assetName ||
      '',
  } as ILineupProductVariationInfo);

export const getLineupFallbackProducts = (selectedProducts: IProduct[]) => {
  const productsInfo: IProducts = {};

  selectedProducts.forEach((product) => {
    const {
      product_code: productCode,
      name_override: nameOverride,
      active = true,
      assets,
      asset_name: assetNames,
      hqasset_name: hqAssetNames,
      productVariations = [],
    } = product;

    if (!productCode) {
      return;
    }

    productsInfo[productCode] = {
      productCode,
      nameOverride: nameOverride ?? null,
      active,
      assets: {
        default: [
          assets?.[0]?.name || assetNames?.[0] || hqAssetNames?.[0] || '',
        ],
      },
      variations: Object.fromEntries(
        productVariations.map((variation) => [
          variation.product_code,
          getLineupFallbackVariation(variation),
        ]),
      ),
    };
  });

  return productsInfo;
};

export const getBaseOverrideFilters = (filters: string[]) =>
  filters.filter(
    (filter) =>
      !filter.startsWith(SEARCH_FILTER_PREFIX) && filter !== SEARCH_TAG_FILTER,
  );

export const buildMatchedComponentsSearchFilters = (searchKeyword: string) => {
  const keyword = searchKeyword.trim();

  if (!keyword) {
    return [];
  }

  return [
    `name.regexp=${encodeURIComponent(escapeRegExp(keyword))}`,
    SEARCH_TAG_FILTER,
  ];
};

export const isPlaybackDisabled = (
  activeAssembly: unknown,
  totalPositions: number,
): boolean => !activeAssembly || totalPositions <= 1;

export const getRotationCarouselDisabledState = (
  activeAssembly: any,
  selectedPosition: string | number,
  positions: number[],
  _status?: RotationEngineStatus,
): RotationCarouselDisabledState => {
  if (!activeAssembly || !positions.includes(Number(selectedPosition))) {
    return true;
  }

  return {
    left: Number(selectedPosition) === positions[0],
    right: Number(selectedPosition) === positions[positions.length - 1],
  };
};

export interface ScheduleItemOverrideInput {
  id: string;
  endDate: string | null;
  startDate: string;
  componentId: number;
  scheduleItemId: string | number;
  componentConfig?: IGcfComponentConfig;
  scheduleItemOverrideProductVariations?: {
    code: number | string;
  }[];
}

export const buildScheduleItemOverrideChangeItem = ({
  override,
}: {
  override: ScheduleItemOverrideInput;
}): ChangeItem => {
  return {
    type: ChangeTypeEnum.SCHEDULE_ITEM_OVERRIDE,
    op: ChangeOperation.INSERT,
    id: null,
    data: {
      id: override.id,
      end_date: override.endDate
        ? dayjs(override.endDate).format('YYYY-MM-DD')
        : null,
      start_date: dayjs(override.startDate).format('YYYY-MM-DD'),
      schedule_item_id: String(override.scheduleItemId),
      ...(override.componentConfig && {
        component_config: override.componentConfig,
      }),
      ...(override.scheduleItemOverrideProductVariations?.length
        ? {
            schedule_item_override_product_variations:
              override.scheduleItemOverrideProductVariations,
          }
        : { component_id: override.componentId }),
    },
  };
};

export const buildScheduleItemOverrideChangeSetPayload = ({
  locationGroupId,
  locationCode,
  daypartId,
  override,
}: {
  locationGroupId: number;
  locationCode: string;
  daypartId: number;
  override: ScheduleItemOverrideInput;
}): ChangeSetPayload => {
  return {
    location_group_ids: [locationGroupId],
    location_code: locationCode,
    daypart_id: daypartId,
    changes: [buildScheduleItemOverrideChangeItem({ override })],
  };
};

const getMainComponent = (components: IAssemblyComponent[]) => {
  return components.find((component) => component.backupOrder === 0);
};

export const getApplicableOverrides = (
  scheduleItemId: number,
  currentScheduleOverrideId: number,
  date: string,
  components: IComponents,
  overrides: IScheduleItemOverrides,
): ISelectedSectionDetailInfo[] => {
  const allOverrides = overrides?.[scheduleItemId] ?? [];

  return allOverrides
    .filter((override) => typeof override.scheduleItemOverrideId === 'number')
    .filter((override) =>
      dayjs(override.endDate as string).isSameOrAfter(dayjs(date)),
    )
    .filter(
      (override) =>
        override.scheduleItemOverrideId !== currentScheduleOverrideId,
    )
    .toSorted((a, b) =>
      dayjs(a.modifiedAt).isAfter(dayjs(b.modifiedAt)) ? -1 : 1,
    )
    .map(({ scheduleItemOverrideId, componentId, startDate, endDate }) => {
      const scheduleOverrideComponent = isNil(componentId)
        ? undefined
        : components[componentId];

      return {
        id: `${scheduleItemId}-${scheduleItemOverrideId}`,
        scheduleOverrideId: scheduleItemOverrideId,
        scheduleItemId,
        componentId: componentId,
        component: scheduleOverrideComponent,
        startDate: dayjs(startDate).format(US_DATE_SLASH_FORMAT),
        endDate: endDate ? dayjs(endDate).format(US_DATE_SLASH_FORMAT) : null,
        isVideoComponent:
          getFileExtensionType(
            scheduleOverrideComponent?.config?.background,
          ) === FileTypes.VIDEO,
        type: EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE,
      };
    });
};

export const getScheduleOverrideStatus = (
  scheduleOverrideId: string | number,
) =>
  typeof scheduleOverrideId === 'string' &&
  scheduleOverrideId.startsWith('draft-');

export const handleBuildSelectedSectionInfo = (
  event: ComponentInstanceData,
  components: IComponents,
  overrides: IScheduleItemOverrides,
  date: string,
): ISelectedSectionDetailInfo[] => {
  const selectedSectionType = event.target;
  let selectedSectionInfo: ISelectedSectionDetailInfo[] = [];

  switch (selectedSectionType) {
    case EComponentInstanceDataTarget.ASSEMBLY: {
      const { id, startDate, componentId, modifiedAt } = getMainComponent(
        event.section.components,
      ) as IAssemblyComponent;
      const component = components[componentId];
      selectedSectionInfo = [
        {
          id,
          componentId,
          component,
          startDate: dayjs(startDate).format(US_DATE_SLASH_FORMAT),
          endDate: null,
          modifiedAt: dayjs(modifiedAt).format(US_DATE_SLASH_FORMAT),
          backups: event.backups?.map((backup) => ({
            ...backup,
            component: components[backup.componentId],
          })),
          allowOverrides: false,
          isVideoComponent:
            getFileExtensionType(component?.config?.background) ===
            FileTypes.VIDEO,
          type: EComponentInstanceDataTarget.ASSEMBLY,
        },
      ];
      break;
    }
    case EComponentInstanceDataTarget.SCHEDULE_ITEM: {
      const {
        id,
        startDate,
        endDate,
        componentId,
        modifiedAt,
        allowOverrides,
      } = getMainComponent(event.section.components) as IAssemblyComponent;
      const component = components[componentId];
      selectedSectionInfo = [
        {
          id,
          componentId,
          component,
          startDate: dayjs(startDate).format(US_DATE_SLASH_FORMAT),
          endDate: endDate ? dayjs(endDate).format(US_DATE_SLASH_FORMAT) : null,
          modifiedAt: dayjs(modifiedAt).format(US_DATE_SLASH_FORMAT),
          backups: event.backups?.map((backup) => ({
            ...backup,
            component: components[backup.componentId],
          })),
          allowOverrides: !!allowOverrides,
          isVideoComponent:
            getFileExtensionType(component?.config?.background) ===
            FileTypes.VIDEO,
          type: EComponentInstanceDataTarget.SCHEDULE_ITEM,
        },
      ];
      break;
    }
    case EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE: {
      const {
        id: scheduleItemId,
        componentId: scheduleItemComponentId,
        startDate,
        endDate,
        modifiedAt,
      } = event.parent as IScheduleSection;
      const {
        id: scheduleOverrideId,
        startDate: scheduleOverrideStartDate,
        endDate: scheduleOverrideEndDate,
        componentId: scheduleOverrideComponentId,
      } = event.ref;
      const scheduleItemComponent = components[scheduleItemComponentId];
      const scheduleOverrideComponent = components[scheduleOverrideComponentId];

      // Get the other applicable but inactive overrides for the same schedule item, excluding the currently selected override
      const applicableOverrides = getApplicableOverrides(
        scheduleItemId,
        scheduleOverrideId,
        date,
        components,
        overrides,
      );

      const isOverrideNewlyCreated =
        getScheduleOverrideStatus(scheduleOverrideId);

      selectedSectionInfo = [
        ...(!isOverrideNewlyCreated
          ? [
              {
                id: `${scheduleItemId}-${scheduleOverrideId}`,
                scheduleOverrideId,
                scheduleItemId,
                componentId: scheduleOverrideComponentId,
                component: scheduleOverrideComponent,
                startDate: dayjs(scheduleOverrideStartDate).format(
                  US_DATE_SLASH_FORMAT,
                ),
                endDate: scheduleOverrideEndDate
                  ? dayjs(scheduleOverrideEndDate).format(US_DATE_SLASH_FORMAT)
                  : null,
                isVideoComponent:
                  getFileExtensionType(
                    scheduleOverrideComponent?.config?.background,
                  ) === FileTypes.VIDEO,
                type: EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE,
              },
            ]
          : []),
        ...applicableOverrides,
        {
          id: scheduleItemId,
          componentId: scheduleItemComponentId,
          component: scheduleItemComponent,
          startDate: dayjs(startDate).format(US_DATE_SLASH_FORMAT),
          endDate: endDate ? dayjs(endDate).format(US_DATE_SLASH_FORMAT) : null,
          modifiedAt: dayjs(modifiedAt).format(US_DATE_SLASH_FORMAT),
          backups: event.backups?.map((backup) => ({
            ...backup,
            component: components[backup.componentId],
          })),
          allowOverrides: true,
          isVideoComponent:
            getFileExtensionType(scheduleItemComponent?.config?.background) ===
            FileTypes.VIDEO,
          type: EComponentInstanceDataTarget.SCHEDULE_ITEM,
        },
      ];
      break;
    }
    default:
      break;
  }

  return selectedSectionInfo;
};

export const buildSelectedOverrideDetailInfo = (
  selectedScheduleItem: ISelectedSectionDetailInfo,
  component: IComponent,
  startDate: string,
  overrideId?: string | number,
  endDate: string | null = null,
): ISelectedSectionDetailInfo => ({
  id: overrideId ?? uid(),
  scheduleItemId: Number(selectedScheduleItem.id),
  componentId: component.id,
  component,
  startDate: startDate,
  endDate,
  isDraft: false,
  isVideoComponent:
    getFileExtensionType(component.config.background) === FileTypes.VIDEO,
  type: EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE,
});

export const buildDraftOverrideDetailInfo = (
  selectedScheduleItem: ISelectedSectionDetailInfo,
  startDate: string,
  extra?: Partial<ISelectedSectionDetailInfo>,
): ISelectedSectionDetailInfo => ({
  id: `draft-${uid()}`,
  scheduleItemId: Number(selectedScheduleItem.id),
  // Draft overrides are created before a replacement promo is chosen.
  componentId: undefined,
  component: undefined,
  startDate,
  endDate: null,
  isDraft: true,
  isVideoComponent: false,
  type: EComponentInstanceDataTarget.SCHEDULE_ITEM_OVERRIDE,
  ...(extra ?? {}),
});

const seedProductDefaultVariationAssetName = (
  mappedProduct: IGcfProductListProduct,
  sourceProduct: IProduct,
  themeName?: string,
) => {
  const defaultSize = mappedProduct.config?.defaultSize;
  const defaultSizeVariations = defaultSize
    ? mappedProduct.sizes?.[defaultSize]?.variations
    : undefined;

  const mappedVariationMissingAssetName = Object.values(
    defaultSizeVariations ?? {},
  ).find((variation) =>
    variation.config?.images?.some(
      (image) => image.visible && !image.assetName,
    ),
  );

  const selectedVariationWithAsset = sourceProduct.productVariations?.find(
    (variation) =>
      variation.product_code === mappedVariationMissingAssetName?.productCode,
  );

  const themedVariationAsset = selectedVariationWithAsset?.assets?.find(
    (asset) => {
      const theme = asset.themes?.find((theme) => theme.name === themeName);

      return theme ? asset.theme_id?.includes(theme.id) : false;
    },
  );
  const assetName = themeName
    ? themedVariationAsset?.name || sourceProduct.assets?.[0]?.name
    : selectedVariationWithAsset?.assets?.[0]?.name ||
      sourceProduct.assets?.[0]?.name;
  const image = mappedVariationMissingAssetName?.config?.images?.find(
    (image) => image.visible && !image.assetName,
  );

  if (image && assetName) {
    image.assetName = assetName;
  }
};

export const addProductsToLineupComponent = (
  component: IComponent,
  groupKey: string,
  productsToAdd: IProduct[],
  themeName?: string,
): { component: IComponent; addedProductUids: string[] } => {
  const groupOrder = Number(groupKey);
  const componentConfig = component.config as IGcfComponentConfig;
  const productListGroups = componentConfig?.productList?.groups ?? {};

  if (!componentConfig?.productList || !groupOrder || !productsToAdd.length) {
    return { component, addedProductUids: [] };
  }

  const filteredProductsToAdd = productsToAdd.filter(
    (product) => product.product_code,
  );

  if (!filteredProductsToAdd.length) {
    return { component, addedProductUids: [] };
  }

  let hasAddedProducts = false;
  const addedProductUids: string[] = [];
  const buildUpdatedGroup = (group: IGcfProductListGroup) => {
    const currentProducts = Object.values(group.products ?? {});
    const currentBackups = Object.values(group.backups ?? {});
    const maxProductOrder = currentProducts.reduce(
      (maxOrder, product) => Math.max(maxOrder, product.order ?? 0),
      0,
    );
    const newProducts = filteredProductsToAdd.map((product, index) => {
      const productOrder = maxProductOrder + index + 1;
      const mappedProduct = mapProductToGCFGroupProduct(
        product,
        productOrder,
        true,
        {
          showCalories: true,
          showLabel: true,
          showPrice: true,
          showFlavors: true,
        },
      );
      seedProductDefaultVariationAssetName(mappedProduct, product, themeName);
      const mappedProductConfig = { ...mappedProduct.config };

      delete mappedProductConfig.isBackupProduct;

      const newProduct = {
        ...mappedProduct,
        config: mappedProductConfig,
      };

      addedProductUids.push(newProduct.uid ?? '');

      return newProduct;
    });

    hasAddedProducts = true;

    return mapGcfGroupProducts(
      group.order,
      group,
      [...currentProducts, ...newProducts],
      currentBackups,
      false,
    );
  };

  let nextGroups = Object.fromEntries(
    Object.entries(productListGroups).map(([productListGroupKey, group]) => {
      if (group.order !== groupOrder) {
        return [productListGroupKey, group];
      }

      return [productListGroupKey, buildUpdatedGroup(group)];
    }),
  );

  if (!hasAddedProducts && component.type === ComponentTypes.SINGLE_SHELF) {
    nextGroups = {
      ...nextGroups,
      [String(groupOrder)]: buildUpdatedGroup({
        order: groupOrder,
        config: {},
        products: {},
        backups: {},
      }),
    };
  }

  if (!hasAddedProducts) {
    return { component, addedProductUids: [] };
  }

  return {
    component: {
      ...component,
      config: {
        ...componentConfig,
        productList: {
          ...componentConfig.productList,
          groups: nextGroups,
        },
      },
    },
    addedProductUids,
  };
};

export const removeProductFromLineupComponent = (
  component: IComponent,
  productKey: string,
): IComponent => {
  const componentConfig = component.config as IGcfComponentConfig;

  const found = findProductAndGroupByUid(componentConfig, productKey);
  if (!found) {
    return component;
  }

  const { group, groupKey } = found;
  const currentProducts = Object.values(group.products ?? {});

  // Prevent removing the last product in a group — an empty group breaks the lineup display
  if (currentProducts.length <= 1) {
    return component;
  }

  const nextProducts = currentProducts.filter(
    (product) => product.uid !== productKey,
  );

  const nextGroups = {
    ...componentConfig.productList.groups,
    [groupKey]: mapGcfGroupProducts(
      group.order,
      group,
      nextProducts,
      Object.values(group.backups ?? {}),
      false,
    ),
  };

  return {
    ...component,
    config: {
      ...componentConfig,
      productList: {
        ...componentConfig.productList,
        groups: nextGroups,
      },
    },
  };
};

export const hasOverrideDateValidationError = (
  selectedScheduleItem?: ISelectedSectionDetailInfo,
  overrideItem?: ISelectedSectionDetailInfo,
) => {
  if (!selectedScheduleItem || !overrideItem?.component) {
    return false;
  }

  let startDate = overrideItem.startDate ? dayjs(overrideItem.startDate) : null;
  let endDate = overrideItem.endDate ? dayjs(overrideItem.endDate) : null;

  if (startDate && !startDate.isValid()) {
    startDate = null;
  }

  if (endDate && !endDate.isValid()) {
    endDate = null;
  }
  const overrideComponent = overrideItem.component as any;
  const hasBaseValidationError = !!onDateRangeValidate(
    [startDate, endDate],
    true,
    selectedScheduleItem.startDate,
    selectedScheduleItem.endDate,
    overrideComponent.end_date,
  );

  if (hasBaseValidationError) {
    return true;
  }

  const { startDateMin, startDateMax, endDateMin, endDateMax } =
    getOverrideDatePickerLimits(selectedScheduleItem, overrideItem);

  const isStartDateBeforeMin = startDateMin
    ? startDate?.isBefore(startDateMin, 'day')
    : false;
  const isStartDateAfterMax = startDateMax
    ? startDate?.isAfter(startDateMax, 'day')
    : false;

  const isStartDateOutOfRange = !!(isStartDateBeforeMin || isStartDateAfterMax);

  const isEndDateBeforeMin = endDateMin
    ? endDate?.isBefore(endDateMin, 'day')
    : false;
  const isEndDateAfterMax = endDateMax
    ? endDate?.isAfter(endDateMax, 'day')
    : false;
  const isEndDateOutOfRange = !!(isEndDateBeforeMin || isEndDateAfterMax);

  return isStartDateOutOfRange || isEndDateOutOfRange;
};

export const haveOverridesInvalid = (
  overrideValidationChips: IOverrideValidationChips[],
) => overrideValidationChips.some((chip) => chip.chipType === CHIP_TYPE.error);

export const haveChangeSetIdInvalid = (
  previewChangeSetIds: IPreviewChangeSetIds[],
  overrideIds: string[],
) => {
  return overrideIds.some(
    (overrideId) =>
      !previewChangeSetIds.find((item) => item.overrideId === overrideId)
        ?.changeSetId,
  );
};

export const shouldDisableSaveButton = (
  overrideValidationChips: IOverrideValidationChips[],
  deletedExistingOverrides: ISelectedSectionDetailInfo[],
  overrideChangeSets: IOverrideChangeSet[],
  deletedPreviewChangeSetIds: IPreviewChangeSetIds[],
) => {
  const deletedOverrideIds = deletedExistingOverrides.map(
    (item) => item.id as string,
  );

  if (overrideValidationChips.length > 0) {
    const previewChangeSetIds: IPreviewChangeSetIds[] = overrideChangeSets.map(
      (changeSet) => ({
        changeSetId: changeSet.previewChangeSetId,
        overrideId: changeSet.changeSetItem.id as string,
      }),
    );
    const createdOverrideIds = overrideValidationChips.map(
      (chip) => chip.id as string,
    );

    return (
      haveOverridesInvalid(overrideValidationChips) ||
      haveChangeSetIdInvalid(previewChangeSetIds, createdOverrideIds) ||
      haveChangeSetIdInvalid(deletedPreviewChangeSetIds, deletedOverrideIds)
    );
  }

  return (
    deletedExistingOverrides.length === 0 ||
    haveChangeSetIdInvalid(deletedPreviewChangeSetIds, deletedOverrideIds)
  );
};

export const buildDeleteScheduleItemOverrideChangeSetPayload = ({
  locationGroupId,
  locationCode,
  daypartId,
  override,
}: {
  locationGroupId: number;
  locationCode: string;
  daypartId: number;
  override: {
    scheduleOverrideId: string | number;
    scheduleItemId: string | number;
  };
}): ChangeSetPayload => {
  const scheduleItemOverrideChange: ChangeItem = {
    type: ChangeTypeEnum.SCHEDULE_ITEM_OVERRIDE,
    op: ChangeOperation.DELETE,
    data: {
      id: Number(override.scheduleOverrideId),
      schedule_item_id: String(override.scheduleItemId),
    },
  };

  return {
    location_group_ids: [locationGroupId],
    location_code: locationCode,
    daypart_id: daypartId,
    changes: [scheduleItemOverrideChange],
  };
};

export const isPromoComponent = (
  selectedScheduleItem: ISelectedSectionDetailInfo,
) => {
  if (!selectedScheduleItem.component) {
    return false;
  }

  return (
    selectedScheduleItem.component.type === ComponentTypes.PROMOTIONAL_SPACE
  );
};

export const isProductLineupComponent = (
  selectedScheduleItem: Partial<ISelectedSectionDetailInfo>,
) => {
  if (!selectedScheduleItem.component) {
    return false;
  }

  return [
    ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS,
    ComponentTypes.LISTING_VISUALS_GALLERY,
    ComponentTypes.SINGLE_SHELF,
  ].includes(selectedScheduleItem.component.type as ComponentTypes);
};

export const hasProductLineupOverride = (
  selectedScheduleItem: ISelectedSectionDetailInfo | undefined,
  overrideChangeSetItems: ISelectedSectionDetailInfo[],
): boolean =>
  !!selectedScheduleItem &&
  isProductLineupComponent(selectedScheduleItem) &&
  overrideChangeSetItems.some((item) => !item.isDraft);

export const getSelectedOverrideProduct = (
  config: IGcfComponentConfig | undefined,
  productKey: string | undefined,
): { productConfig?: IGcfProductListProduct; productCode?: string } => {
  const product = findProductByUid(config, productKey);

  if (!product) {
    return {};
  }

  return { productConfig: product, productCode: product.productCode };
};

/**
 * Regenerates uids for all products in a component config, producing a new
 * component with globally unique uids. Used when cloning a component for a
 * new override card so each card's products have distinct uids.
 */
export const cloneComponentWithFreshUids = (
  component: IComponent,
): IComponent => {
  const config = component.config as IGcfComponentConfig;
  const groups = config?.productList?.groups;

  if (!groups) {
    return component;
  }

  const nextGroups = Object.fromEntries(
    Object.entries(groups).map(([groupKey, group]) => {
      const nextProducts = Object.fromEntries(
        Object.entries(group.products ?? {}).map(([productKey, product]) => [
          productKey,
          { ...product, uid: uid() },
        ]),
      );

      return [groupKey, { ...group, products: nextProducts }];
    }),
  );

  return {
    ...component,
    config: {
      ...config,
      productList: {
        ...config.productList,
        groups: nextGroups,
      },
    },
  };
};

const getProductVariationsAssets = (productData?: IProduct) => {
  if (!productData) return {};

  const productVariationsAssets: IProductVariationsAssets = {};
  const groupAsset = productData.assets?.[0];

  for (const variation of productData.productVariations) {
    productVariationsAssets[variation.product_code] = [
      ...(groupAsset ? [groupAsset] : []),
      ...(variation.assets ?? []),
    ];
  }

  return productVariationsAssets;
};

/**
 * Searches all groups in a component config for a product matching the given uid.
 * Returns the product, its parent group, and the group key if found, undefined otherwise.
 * Used by remove/replace operations that need group context.
 */
export const findProductAndGroupByUid = (
  config: IGcfComponentConfig | undefined,
  productUid: string | undefined,
):
  | {
      product: IGcfProductListProduct;
      group: IGcfProductListGroup;
      groupKey: string;
    }
  | undefined => {
  if (!config?.productList?.groups || !productUid) {
    return undefined;
  }

  for (const [groupKey, group] of Object.entries(config.productList.groups)) {
    for (const product of Object.values(group.products ?? {})) {
      if (product.uid === productUid) {
        return { product, group, groupKey };
      }
    }
  }

  return undefined;
};

/**
 * Searches all groups in a component config for a product matching the given uid.
 * Returns the product if found, undefined otherwise.
 */
export const findProductByUid = (
  config: IGcfComponentConfig | undefined,
  productUid: string | undefined,
): IGcfProductListProduct | undefined =>
  findProductAndGroupByUid(config, productUid)?.product;

/**
 * Derives product attribute data from component config and product API data.
 * Used by both the page component (render) and the auto-persist effect.
 */
export const getProductAttributeData = (
  config: IGcfComponentConfig | undefined,
  selectProductKey: string,
  productData?: IProduct,
  themeName?: string,
): {
  productConfig: IGcfProductListProduct | undefined;
  attributeSizes: IAttributeSize[];
  productVariationsAssets: IProductVariationsAssets;
} => {
  const { productConfig } = getSelectedOverrideProduct(
    config,
    selectProductKey,
  );
  // Group status is the product-level `active` flag; only map variations when
  // the group is active. An inactive group hides all of its variations from
  // the override sidebar, even active variations.
  const attributeSizes = productData?.active
    ? mapVariationsToAttributes(
        productData?.productVariations ?? [],
        productConfig,
        themeName,
        productData?.themes,
      )
    : [];

  const productVariationsAssets = getProductVariationsAssets(productData);

  return { productConfig, attributeSizes, productVariationsAssets };
};

export const updateTargetComponentWithComponentConfig = (
  components: IComponents,
  targetComponentId: number | undefined,
  componentConfigWithNewStructure: any,
): IComponents => {
  if (
    !componentConfigWithNewStructure ||
    !targetComponentId ||
    !components[targetComponentId]
  ) {
    return components;
  }

  return {
    ...components,
    [targetComponentId]: {
      ...components[targetComponentId],
      config:
        componentConfigWithNewStructure ?? components[targetComponentId].config,
    },
  };
};
export const replaceProductInLineupComponent = (
  component: IComponent,
  productKey: string,
  replacementProduct: IProduct,
  overrideImageAttributes: Pick<
    IGcfProductListProductImage,
    'style' | 'outageStamp'
  >,
  themeName?: string,
): { component: IComponent; replacementProductUid: string | undefined } => {
  const componentConfig = component.config as IGcfComponentConfig;

  const found = findProductAndGroupByUid(componentConfig, productKey);
  if (!found) {
    return { component, replacementProductUid: undefined };
  }

  const { group, groupKey } = found;
  let replacementProductUid: string | undefined;

  const nextGroupProducts = Object.fromEntries(
    Object.entries(group.products).map(
      ([groupProductKey, groupProductValue]) => {
        if (groupProductValue.uid !== productKey) {
          return [groupProductKey, groupProductValue];
        }

        const mappedProduct = mapProductToGCFGroupProduct(
          replacementProduct,
          groupProductValue.order,
          true,
          {
            showCalories: true,
            showLabel: true,
            showPrice: true,
            showFlavors: true,
          },
          overrideImageAttributes,
        );
        seedProductDefaultVariationAssetName(
          mappedProduct,
          replacementProduct,
          themeName,
        );
        replacementProductUid = mappedProduct.uid;

        return [
          groupProductKey,
          {
            ...mappedProduct,
            sizes: Object.fromEntries(
              Object.entries(mappedProduct.sizes ?? {}).map(
                ([sizeEntryKey, sizeEntryValue]) => [
                  sizeEntryKey,
                  {
                    ...sizeEntryValue,
                  },
                ],
              ),
            ),
            config: {
              ...mappedProduct.config,
            },
          },
        ];
      },
    ),
  );

  const nextGroups = {
    ...componentConfig.productList.groups,
    [groupKey]: {
      ...group,
      products: nextGroupProducts,
    },
  };

  return {
    component: {
      ...component,
      config: {
        ...componentConfig,
        productList: {
          ...componentConfig.productList,
          groups: nextGroups,
        },
      },
    },
    replacementProductUid,
  };
};
const getReorderedGroupProducts = (
  group: IGcfProductListGroup,
  oldIndex: number,
  newIndex: number,
) => {
  const orderedProducts = Object.values(group.products ?? {}).sort(
    sortItemsByOrder,
  );

  if (
    oldIndex < 0 ||
    newIndex < 0 ||
    oldIndex >= orderedProducts.length ||
    newIndex >= orderedProducts.length
  ) {
    return null;
  }

  const reorderedProducts = moveArrayItem(orderedProducts, oldIndex, newIndex);

  return mapGCFProductArrayToGcfGroupProducts(reorderedProducts, false);
};

export const reorderProductLineupComponent = (
  component: IComponent,
  accordionKey: string,
  oldIndex: number,
  newIndex: number,
): IComponent | undefined => {
  if (oldIndex === newIndex) {
    return component;
  }

  const config = component.config as IGcfComponentConfig | undefined;
  const productListGroups = config?.productList?.groups;

  if (!productListGroups) {
    return component;
  }

  const targetGroupEntry = Object.entries(productListGroups).find(
    ([groupId, group]: [string, IGcfProductListGroup]) =>
      String(group?.order) === accordionKey || groupId === accordionKey,
  );

  if (!targetGroupEntry) {
    return component;
  }

  const [targetGroupId, targetGroup] = targetGroupEntry;
  const reorderedProducts = getReorderedGroupProducts(
    targetGroup,
    oldIndex,
    newIndex,
  );

  if (!reorderedProducts) {
    return component;
  }

  return {
    ...component,
    config: {
      ...component.config,
      productList: {
        ...config?.productList,
        groups: {
          ...productListGroups,
          [targetGroupId]: {
            ...targetGroup,
            products: reorderedProducts,
          },
        },
      },
    },
  };
};

export const getProductImageStyleAndOutageStamp = (
  selectedScheduleItem?: ISelectedSectionDetailInfo,
): IProductImageStyleAndOutageStamp => {
  if (
    !selectedScheduleItem?.component?.config ||
    !isProductLineupComponent(selectedScheduleItem)
  ) {
    return {};
  }

  const componentConfig = selectedScheduleItem.component
    .config as IGcfComponentConfig;
  const productListGroups = componentConfig?.productList?.groups;

  if (!productListGroups) {
    return {};
  }

  const productImageStyle: IProductImageStyleAndOutageStamp = {};

  for (const group of Object.values(productListGroups)) {
    const groupOrder = group.order;
    const products = group.products;

    if (!products) continue;

    for (const product of Object.values(products)) {
      if (!isProductImageVisible(product)) continue;

      const productOrder = product.order;
      const sizes = product.sizes;
      const productKey = `${groupOrder}-${productOrder}`;

      if (!sizes) continue;

      for (const size of Object.values(sizes)) {
        const variations = size.variations;

        if (!variations) continue;

        for (const variation of Object.values(variations)) {
          const images = variation.config?.images;

          if (!images?.length) continue;

          for (const image of images) {
            if (image.visible && image.style) {
              productImageStyle[productKey] = {
                style: image.style,
                outageStamp: image.outageStamp,
              };
            }
          }
        }
      }
    }
  }

  return productImageStyle;
};

export const getImageStyleAndOutageStampBySelectProductKey = (
  selectProductKey: string,
  componentConfig: IGcfComponentConfig,
  productImageStyleAndOutageStamp: IProductImageStyleAndOutageStamp,
): Pick<IGcfProductListProductImage, 'style' | 'outageStamp'> => {
  const productOrderKey = transformProductKeyToOrderKey(
    selectProductKey,
    componentConfig,
  );

  return productImageStyleAndOutageStamp[productOrderKey] ?? {};
};
